<script setup>
import { computed, defineProps } from "vue";

const props = defineProps({
    userCard: {
        type: Object,
        required: true,
    },
});

// Config computeds
const config = computed(() => props.userCard.card.config_visual);
const cardConfigs = computed(() => ({
  stamp: props.userCard.card.config.find((item) => item.type === 'stamp'),
  subscription: props.userCard.card.config.find((item) => item.type === 'subscription')
}));

// Current card type and steps
const currentCardType = computed(() => {
  if (cardConfigs.value.subscription) return 'subscription';
  if (cardConfigs.value.stamp) return 'stamp';
  return undefined;
});

const stepValues = computed(() => {
  if (!currentCardType.value) return undefined;
  
  if (currentCardType.value === 'stamp') {
    return cardConfigs.value.stamp?.steps.map((item) => item.key) || [];
  }
  
  // For subscription, return all steps as special
  const maxSteps = Number(cardConfigs.value.subscription?.steps[0]?.value) || 0;
  return Array.from({ length: maxSteps }, (_, i) => i + 1);
});

const steps = computed(() => {
  if (!currentCardType.value) return undefined;

  const currentVisits = props.userCard.stamps;
  const maxVisibleSteps = 10;

  if (currentCardType.value === 'subscription') {
    const maxSteps = Number(cardConfigs.value.subscription?.steps[0]?.value) || 0;
    const totalSteps = Math.min(maxSteps, maxVisibleSteps);
    
    // Always show first steps for subscription cards
    return Array.from({ length: totalSteps }, (_, i) => i + 1);
  }

  // Stamp card logic
  const stampSteps = cardConfigs.value.stamp?.steps || [];
  const maxStepValue = stampSteps.reduce((max, item) => Math.max(max, item.key ?? 0), 0);
  
  let start = Math.max(1, currentVisits - maxVisibleSteps + 1);
  if (maxStepValue < maxVisibleSteps) start = 1;
  
  const end = Math.min(start + maxVisibleSteps - 1, maxStepValue);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

// Emoji display logic
const getStepEmoji = (step) => {
  if (!stepValues.value) return config.value.image_step || '◻️';
  
  const isSpecialStep = stepValues.value.includes(step);
  const isCompleted = Math.abs(props.userCard.stamps) >= step;
  
  if (currentCardType.value === 'subscription') {
    return isCompleted 
      ? config.value.image_marked_step || '✅' 
      : config.value.image_step || '◻️';
  }
  
  // Stamp card logic
  if (isSpecialStep && isCompleted) {
    return config.value.image_marked_gift || '🎉';
  }
  if (isCompleted) {
    return config.value.image_marked_step || '✅';
  }
  if (isSpecialStep) {
    return config.value.image_gift || '🎁';
  }
  return config.value.image_step || '◻️';
};
</script>

<template>
  <div 
    class="card"
    :style="`
      --text-primary: ${config.text_color};
      --text-label: ${config.label_color ?? config.text_color};
      background-color: ${config.background_color};
    `"
  >
    <div class="card_header">
      <img v-if="config.logo" height="35" :src="config.logo" alt="logo" />
      <div class="card_header_text">
        <div v-if="userCard.card_view.header_left_title && userCard.card_view.header_left_value" class="card_header_text_item">
          <span class="mr-9-prim ttu medium">{{ userCard.card_view.header_left_title }}</span>
          <span class="mr-14-prim medium">{{ userCard.card_view.header_left_value }}</span>
        </div>
        <div v-if="userCard.card_view.header_right_title && userCard.card_view.header_right_value" class="card_header_text_item">
          <span class="mr-9-prim ttu medium">{{ userCard.card_view.header_right_title }}</span>
          <span class="mr-14-prim medium">{{ userCard.card_view.header_right_value }}</span>
        </div>
      </div>
    </div>
    <div class="card_data">
      <ul v-if="steps && steps.length > 0" class="card_data_presents">
        <li
          v-for="(step, index) in steps"
          :key="index"
          class="card_data_presents_item"
        >
          <span class="step-emoji" :title="`${'Step'} ${step}`">
            {{ getStepEmoji(step) }}
          </span>
        </li>
      </ul>
      <img 
        class="card_data_cover"
        v-else-if="config.backgound_file" 
        :src="config.backgound_file" 
        alt="cover" 
      />
      <div v-else class="card_data_empty"></div>
      <div class="card_data_mid">
        <div v-if="userCard.card_view.left_title && userCard.card_view.left_value" class="card_data_mid_item">
          <span class="mr-9-label ttu medium">{{ userCard.card_view.left_title }}</span>
          <span class="mr-14-prim medium ttl">{{ userCard.card_view.left_value }}</span>
        </div>
        <div v-if="userCard.card_view.right_title && userCard.card_view.right_value" class="card_data_mid_item_end">
          <span class="mr-9-label ttu medium">{{ userCard.card_view.right_title }}</span>
          <span class="mr-14-prim medium">{{ userCard.card_view.right_value }}</span>
        </div>
      </div>
    </div>
    <img 
      v-if="userCard.lending_qr" 
      :src="userCard.lending_qr" 
      height="120"
      alt="qr"
    >
    <div v-else class="card_empty_qr"></div>
  </div>
</template>

<style scoped lang="scss">
.card {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 270px;
  height: fit-content;
  display: flex;
  box-sizing: border-box;
  padding-top: 12px;
  padding-bottom: 12px;
  padding-bottom: 18px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow: 0px 9px 19px 0px rgba(0, 0, 0, 0.13);
  border-radius: 12px;
  &_header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 6px;
    padding: 0px 16px;
    box-sizing: border-box;

    & img {
        border-radius: 4px;
    }

    &_text {
      display: flex;
      gap: 14px;
      width: 100%;
      justify-content: flex-end;
      text-align: end;

      &_item {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
      }
    }
  }

  &_data {
    width: 100%;

    &_cover {
      width: 100%;
      height: 100px;
    }
    &_presents {
      display: flex;
      gap: 10px;
      align-items: center;
      flex-wrap: wrap;
      margin: 0px 15px 15px 15px;
      list-style: none;
      padding: 0;

      &_item {
        .step-emoji {
          font-size: 24px;
          display: inline-block;
          width: 40px;
          height: 40px;
          line-height: 40px;
          text-align: center;
        }
      }
    }

    &_mid {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 14px;
      padding: 8px 16px 0px 16px;
      box-sizing: border-box;

      &_item {
        display: flex;
        flex-direction: column;
        width: 50%;
        &_end {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }
      }
    }

    &_empty {
      width: 100%;
      height: 100px;
      background: rgba(78, 127, 255, 0.25);
      margin-bottom: 12px;
    }
  }

  &_empty_qr {
    padding-bottom: 140px;
  }
}
</style>
